'use client';

import { ArrowUpDown, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface FloatingFilterProps {
  /** Called when the user taps the "Sort" pill. */
  onSort: () => void;
  /** Called when the user taps the "Filter" pill. */
  onFilter: () => void;
  /** The element whose bottom edge triggers the floating filter to appear. */
  anchorRef: React.RefObject<HTMLElement | null>;
}

/**
 * Facebook-style floating filter pill that appears at the bottom of the
 * viewport once the user scrolls past the inline filter controls and is
 * scrolling upward (toward the top of the page). Mirrors the native app's
 * `FloatingFilter` behaviour.
 */
export default function FloatingFilter({
  onSort,
  onFilter,
  anchorRef,
}: FloatingFilterProps) {
  const [visible, setVisible] = useState(false);
  const prevScrollY = useRef(0);
  const ticking = useRef(false);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const scrollY = window.scrollY;
    const delta = scrollY - prevScrollY.current;
    prevScrollY.current = scrollY;

    // Only consider the page "scrolled past" once we're past the anchor
    // (the inline filter controls).
    const anchorBottom =
      anchor.getBoundingClientRect().bottom + window.scrollY;
    const pastAnchor = scrollY > anchorBottom;

    // Show when scrolling down (positive delta = finger moves up, content
    // scrolls down). Hide when scrolling up. Small deltas are ignored to
    // avoid flicker from bounce / momentum.
    const SCROLL_DELTA_THRESHOLD = 4;
    if (pastAnchor && delta > SCROLL_DELTA_THRESHOLD) {
      setVisible(true);
    } else if (delta < -SCROLL_DELTA_THRESHOLD) {
      setVisible(false);
    }

    ticking.current = false;
  }, [anchorRef]);

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [update]);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-12 z-50 flex justify-center sm:hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(2rem)',
        transition: 'opacity 250ms ease-out, transform 250ms ease-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className="pointer-events-auto flex h-[5.2rem] items-center gap-[2.8rem] rounded-full bg-brand-black px-[3rem]">
        <button
          type="button"
          onClick={onSort}
          className="flex items-center gap-[0.8rem] text-[1.6rem] font-semibold text-white"
        >
          <ArrowUpDown className="h-[1.8rem] w-[1.8rem]" />
          Sort
        </button>
        <div className="h-full w-px bg-zinc-600" />
        <button
          type="button"
          onClick={onFilter}
          className="flex items-center gap-[0.8rem] text-[1.6rem] font-semibold text-white"
        >
          <SlidersHorizontal className="h-[1.8rem] w-[1.8rem]" />
          Filter
        </button>
      </div>
    </div>
  );
}
